'use client';

import Image from 'next/image';

import {Icon} from '@udemy-v2/icons';
import {LocalizedHtml, useFormatDate, useI18n} from '@udemy-v2/i18n';
import {useTeamSubscriptionPlan} from '@udemy-v2/subscription-graphql';

import {UDEMY_MASTER_SERVICES_AGREEMENT_URL} from '@/features/organization-team-plan/utils/udemy-legal-urls';

import styles from './team-plan-sale-banner.module.scss';

const cdnSrc = (path: string) => {
    const prefix = process.env.ASSET_PREFIX ?? '';
    return prefix.startsWith('http') ? `${prefix.replace(/\/$/, '')}${path}` : path;
};

// TODO: the campaign end date isn't exposed on selectedDiscount (subscriptionDiscountInterval
// describes the discounted-price billing duration, not the sale window) - hardcoded to match the
// Aug 18 - Sep 23 2026 campaign config in service-pricing until a real field is available.
const SALE_END_DATE = new Date(2026, 8, 23); // months are 0-indexed

/**
 * Sale banner shown on the team plan signup pages (buy-team-plan, free-trial) while a
 * Team Plan discount is active. Renders nothing until the pricing service populates a
 * discount on the TEAM subscription plan, so it's a no-op outside of sale windows.
 */
export function TeamPlanSaleBanner({buyNow = false}: {buyNow?: boolean}) {
    const {gettext, interpolate} = useI18n();
    const {toLocaleDateString} = useFormatDate();
    const teamPlanContext = useTeamSubscriptionPlan();

    if (!teamPlanContext || teamPlanContext.isLoading || teamPlanContext.isSubscriber) {
        return null;
    }

    const discount = teamPlanContext.selectedDiscount;
    if (!discount) {
        return null;
    }

    // Prefer adapter-formatted prices (plan currency from pricing) over re-formatting
    // with user.country, which defaults to USD for anonymous visitors.
    const originalPrice = discount.formattedSubscriptionNonDiscountPrice;
    const discountedPrice = discount.formattedSubscriptionDiscountPrice;
    const saleEndDate = toLocaleDateString(SALE_END_DATE, {
        month: 'short',
        day: 'numeric',
    });
    const subheadlineHtml = buyNow
        ? gettext(
              'Stop hiring to fill skill gaps. Upskill today. <a class="terms">Terms apply.</a>',
          )
        : gettext(
              'Learning for teams across your org — try it free. <a class="terms">Terms apply.</a>',
          );

    return (
        <div className={styles.banner} data-testid="team-plan-sale-banner">
            <Image
                className={styles.graphic}
                src={cdnSrc('/team-plan-sale-banner/brush-stroke.png')}
                alt=""
                width={478}
                height={124}
                unoptimized
            />
            <div className={styles.content}>
                <div className={styles.icon}>
                    <Icon glyph="deal" label={false} size="large" color="knockout" />
                </div>
                <div className={styles.copy}>
                    <p className={styles.headline}>
                        {discount.subscriptionDiscountPercent ? (
                            <LocalizedHtml
                                html={interpolate(
                                    gettext(
                                        '<span class="lead">Score %(percent)s off Team Plan through</span> <span class="date">%(endDate)s</span>',
                                    ),
                                    {
                                        percent: `${discount.subscriptionDiscountPercent}%`,
                                        endDate: saleEndDate,
                                    },
                                    true,
                                )}
                                interpolate={{
                                    lead: <span className={styles['headline-lead']} />,
                                    date: <span className={styles['headline-date']} />,
                                }}
                            />
                        ) : (
                            gettext('Team Plan is on sale for a limited time')
                        )}
                    </p>
                    <LocalizedHtml
                        className={styles.subheadline}
                        html={subheadlineHtml}
                        interpolate={{
                            terms: (
                                <a
                                    href={UDEMY_MASTER_SERVICES_AGREEMENT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ),
                        }}
                    />
                </div>
            </div>
            <div className={styles.price}>
                <p className={styles['original-price']}>{originalPrice}</p>
                <p className={styles['price-values']}>
                    <span className={styles['discounted-price']}>{discountedPrice}</span>{' '}
                    <span className={styles['price-unit']}>{gettext('per user')}</span>
                </p>
                <p className={styles['billed-annually']}>
                    <span className={styles['billed-annually-desktop']}>
                        {gettext('Billed annually')}
                    </span>
                    <span className={styles['billed-annually-mobile']}>
                        {gettext('billed annually')}
                    </span>
                </p>
            </div>
        </div>
    );
}
